import { useMemo, useState } from 'react';
import { stageLabel } from '@ouradent/shared';
import { useSession } from '../auth/SessionProvider';
import { useBoard, useCalls, useInbox, useOverview, useTasks } from '../lib/data';
import { dayDiff, fShort, greeting } from '../lib/format';
import { useLocalState } from '../lib/useLocalState';
import type { BoardLead, InboxRow } from '../lib/types';
import { Icon, type IconName } from '../ui/icons';
import { Chead, ErrorBox, RouteBar, StepPill, useDrawer, useWho, ViewAll } from '../ui/kit';
import { Priorities } from './overview/Priorities';
import {
  AdsCard,
  ClientsCard,
  Funnel,
  Kpis,
  Meetings,
  TeamCard,
  Warnings,
} from './overview/Sections';
import { StepRow, TaskRow } from './rows';

type Filter = 'all' | 'calls' | 'over' | 'step';
const CHIPS: [Filter, string, string | null][] = [
  ['all', 'All', null],
  ['calls', 'Call reviews', 'var(--purple)'],
  ['over', 'Promises', 'var(--orange)'],
  ['step', 'No next step', 'var(--teal)'],
];

export function Today() {
  const { me, can } = useSession();
  const who = useWho();
  const { open } = useDrawer();
  const [scope, setScope] = useLocalState<'mine' | 'team'>('today.scope', 'team');
  const [filter, setFilter] = useLocalState<Filter>('today.filter', 'all');

  const inbox = useInbox();
  const board = useBoard(can('pipeline'));
  const tasks = useTasks(can('tasks') || can('pipeline'));
  const calls = useCalls(can('calls'));
  const overview = useOverview(can('pipeline'));

  const mine = scope === 'mine';
  const leads = useMemo(() => new Map((board.data ?? []).map((l) => [l.id, l])), [board.data]);
  const callsById = useMemo(() => new Map((calls.data ?? []).map((c) => [c.id, c])), [calls.data]);

  const items = (inbox.data ?? []).filter(
    (i) => i.kind === 'call' || !mine || i.owner_id === me?.id,
  );
  const chips = CHIPS.filter(([id]) => id === 'all' || items.some((i) => chipOf(i) === id));
  const list = items.filter((i) => filter === 'all' || chipOf(i) === filter);
  const [all, setAll] = useState(false);
  const shown = all ? list : list.slice(0, INBOX_LIMIT);

  const openTasks = (tasks.data ?? []).filter(
    (t) => !t.done_at && (!mine || t.owner_id === me?.id),
  );
  const todayTasks = openTasks.filter(
    (t) => new Date(t.due_at) >= new Date() && dayDiff(t.due_at) === 0,
  );
  const mySteps = (board.data ?? []).filter(
    (l) => l.step_state === 'ok' && (!mine || l.step_owner === me?.id),
  );
  const stepsToday = mySteps.filter((l) => dayDiff(l.step_due!) === 0);
  const upcoming = [...mySteps].sort((a, b) => +new Date(a.step_due!) - +new Date(b.step_due!))[0];
  const schedule = [
    ...stepsToday.map((l) => ({ at: l.step_due!, el: <StepRow key={`s${l.id}`} lead={l} /> })),
    ...todayTasks.map((t) => ({
      at: t.due_at,
      el: <TaskRow key={t.id} task={t} lead={t.lead_id ? leads.get(t.lead_id) : undefined} />,
    })),
  ].sort((a, b) => +new Date(a.at) - +new Date(b.at));

  function act(i: InboxRow) {
    if (i.kind === 'call') open({ type: 'call', id: i.ref_id });
    else if (i.lead_id) open({ type: 'lead', id: i.lead_id });
  }

  return (
    <>
      <div className="head">
        <div>
          <h1>
            {greeting()}, <span className="grad">{me?.fullName.split(' ')[0]}</span>
          </h1>
          <p>
            {items.length ? `${items.length} things need you today.` : 'You are all caught up.'}
          </p>
        </div>
        <div className="head-r">
          <div className="seg" role="group" aria-label="Scope">
            <button aria-pressed={mine} onClick={() => setScope('mine')}>
              Mine
            </button>
            <button aria-pressed={!mine} onClick={() => setScope('team')}>
              Whole team
            </button>
          </div>
        </div>
      </div>

      <ErrorBox error={inbox.error ?? overview.error} />
      {can('integrations') && <Warnings />}
      <Priorities />
      {can('pipeline') && <Kpis />}

      <div className="grid">
        <div className="col">
          <section className="card fill" aria-label="Action inbox">
            <Chead label="Action inbox" cls="g">
              <span className="viewall" style={{ cursor: 'default' }}>
                {list.length} open
              </span>
            </Chead>
            <div className="fchips">
              {chips.map(([id, label, color]) => (
                <button
                  key={id}
                  className="fchip"
                  aria-pressed={filter === id}
                  onClick={() => setFilter(id)}
                >
                  {color && <i style={{ background: color }} />}
                  {label}{' '}
                  <b>
                    {id === 'all' ? items.length : items.filter((i) => chipOf(i) === id).length}
                  </b>
                </button>
              ))}
            </div>
            {inbox.isLoading ? (
              <div className="loading">Loading…</div>
            ) : list.length ? (
              shown.map((i) => (
                <InboxItem
                  key={`${i.kind}${i.ref_id}`}
                  item={i}
                  lead={i.lead_id ? leads.get(i.lead_id) : undefined}
                  callMins={i.kind === 'call' ? callsById.get(i.ref_id)?.duration_min : null}
                  ownerName={who(i.owner_id).full_name}
                  onClick={() => act(i)}
                />
              ))
            ) : (
              <div className="empty">Nothing here. Nice.</div>
            )}
            {list.length > INBOX_LIMIT && (
              <button className="btn sm ghost inbox-more" onClick={() => setAll(!all)}>
                {all ? 'Show fewer' : `Show all ${list.length}`}
              </button>
            )}
            <div style={{ height: 10 }} />
          </section>
          {can('pipeline') && <Funnel m={overview.data} />}
          {can('pipeline') && <AdsCard leads={board.data ?? []} />}
        </div>

        <div className="col">
          <section className="pass" aria-label="Next step">
            <span className="lbl t">Next step</span>
            {upcoming ? (
              <>
                <RouteBar stage={upcoming.stage} />
                <button className="stepbox" onClick={() => open({ type: 'lead', id: upcoming.id })}>
                  <Icon name="cal" />
                  <span>
                    {upcoming.step_text}, {fShort(upcoming.step_due!)}
                    <small>
                      {upcoming.clinic}
                      {upcoming.city ? `, ${upcoming.city}` : ''}
                    </small>
                  </span>
                  <Icon name="chev" />
                </button>
              </>
            ) : (
              <>
                <RouteBar stage="new" />
                <div className="stepbox">
                  <Icon name="cal" />
                  <span>
                    No upcoming steps<small>Set a next step on your leads</small>
                  </span>
                </div>
              </>
            )}
          </section>
          <section className="card">
            <Chead label="Today" cls="g">
              <ViewAll to="/tasks" />
            </Chead>
            <div className="list-pad">
              {schedule.length ? (
                schedule.map((x) => x.el)
              ) : (
                <div className="empty">No calls or promises scheduled for today.</div>
              )}
            </div>
          </section>
          {can('calendar') && <Meetings />}
          {can('tasks') && <ClientsCard tasks={tasks.data ?? []} />}
          {can('team') && <TeamCard tasks={tasks.data ?? []} leads={board.data ?? []} />}
        </div>
      </div>
    </>
  );
}

const INBOX_LIMIT = 8;

const chipOf = (i: InboxRow): Filter => (i.kind === 'call' ? 'calls' : i.kind);

function InboxItem({
  item,
  lead,
  callMins,
  ownerName,
  onClick,
}: {
  item: InboxRow;
  lead?: BoardLead | undefined;
  callMins?: number | null | undefined;
  ownerName: string;
  onClick: () => void;
}) {
  const kind: Record<InboxRow['kind'], { cls: string; icon: IconName }> = {
    call: { cls: 'k-call', icon: 'video' },
    over: { cls: 'k-over', icon: 'clock' },
    step: { cls: 'k-step', icon: 'user' },
  };
  const k = kind[item.kind];

  let title = item.title;
  let meta = '';
  let tag = null;
  if (item.kind === 'call') {
    meta = `Read.ai call${callMins ? `, ${callMins} min` : ''} · ${lead ? lead.clinic : 'not linked to a lead'}`;
    tag = lead ? (
      <span className="pill sm">To review</span>
    ) : (
      <span className="pill sm red">Not linked</span>
    );
  } else if (item.kind === 'over') {
    meta = `Promised to ${lead ? lead.clinic : 'lead'} · ${ownerName}`;
    tag = <span className="tm late">Overdue</span>;
  } else if (lead) {
    title = `${lead.clinic}: ${lead.step_state === 'missing' ? 'no next step' : 'next step overdue'}`;
    meta = `${stageLabel(lead.stage)}${lead.step_text ? ` · ${lead.step_text}` : ''}`;
    tag = <StepPill lead={lead} />;
  }

  return (
    <button className={`ai ${k.cls}`} onClick={onClick}>
      <span className="aic">
        <Icon name={k.icon} />
      </span>
      <span style={{ minWidth: 0 }}>
        <span className="t" style={{ display: 'block' }}>
          {title}
        </span>
        <span className="m">{meta}</span>
      </span>
      {tag}
      <Icon name="chev" />
    </button>
  );
}
